import React, { useState, useEffect, useCallback } from 'react';
import { InventoryList } from './InventoryList';
import { CreateInventoryForm } from './CreateInventoryForm';
import { UpdateInventoryForm } from './UpdateInventoryForm';
import { ScheduleInventoryForm } from './ScheduleInventoryForm';
import { InventorySlotManager } from './InventorySlotManager';
import { EditInventoryImagesPage } from './EditInventoryImagesPage';
import { InventoryAdminListItem, InventoryDocument } from '../../types';
import { apiService } from '../../services/api';

type ViewMode = 'list' | 'create' | 'edit' | 'schedule' | 'slots' | 'edit-images';

export const InventoryPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [inventories, setInventories] = useState<InventoryAdminListItem[]>([]);
  const [selectedInventory, setSelectedInventory] = useState<InventoryAdminListItem | null>(null);
  const [selectedInventoryDetails, setSelectedInventoryDetails] = useState<InventoryDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load filters from localStorage on mount
  const [statusFilter, setStatusFilter] = useState<number | undefined>(() => {
    const saved = localStorage.getItem('inventory_status_filter');
    return saved !== null ? parseInt(saved) : undefined;
  });
  
  const [variantFilter, setVariantFilter] = useState<string | undefined>(() => {
    const saved = localStorage.getItem('inventory_variant_filter');
    return saved || undefined;
  });

  // Save status filter to localStorage
  const handleStatusFilterChange = (status: number | undefined) => {
    setStatusFilter(status);
    if (status !== undefined) {
      localStorage.setItem('inventory_status_filter', status.toString());
    } else {
      localStorage.removeItem('inventory_status_filter');
    }
  };

  // Save variant filter to localStorage
  const handleVariantFilterChange = (variant: string | undefined) => {
    setVariantFilter(variant);
    if (variant) {
      localStorage.setItem('inventory_variant_filter', variant);
    } else {
      localStorage.removeItem('inventory_variant_filter');
    }
  };

  // Load inventories
  const loadInventories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getInventoryAllItems({
        status: statusFilter,
        variant: variantFilter,
      });
      setInventories(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load inventories');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, variantFilter]);

  useEffect(() => {
    loadInventories();
  }, [loadInventories]);

  const handleCreateInventory = async (name: string) => {
    try {
      setLoading(true);
      await apiService.createInventory({ name });
      await loadInventories();
      setViewMode('list');
    } catch (err: any) {
      setError(err.message || 'Failed to create inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateInventory = async (inventoryId: string, name: string) => {
    try {
      setLoading(true);
      await apiService.duplicateInventory(inventoryId, { name });
      await loadInventories();
      setViewMode('list');
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInventory = async (inventoryId: string, updates: any) => {
    try {
      setLoading(true);
      await apiService.updateInventory(inventoryId, updates);
      await loadInventories();
      setViewMode('list');
    } catch (err: any) {
      setError(err.message || 'Failed to update inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateInventory = async (inventoryId: string) => {
    try {
      setLoading(true);
      await apiService.activateInventory(inventoryId);
      await loadInventories();
    } catch (err: any) {
      setError(err.message || 'Failed to activate inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateInventory = async (inventoryId: string) => {
    try {
      setLoading(true);
      await apiService.deactivateInventory(inventoryId);
      await loadInventories();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate inventory');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishInventory = async (inventoryId: string) => {
    try {
      setLoading(true);
      await apiService.publishInventory(inventoryId);
      await loadInventories();
    } catch (err: any) {
      setError(err.message || 'Failed to publish inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInventory = async (inventory: InventoryAdminListItem) => {
    try {
      setLoading(true);
      const details = await apiService.getInventoryDetailsForUpdate(inventory.id);
      setSelectedInventory(inventory);
      setSelectedInventoryDetails(details);
      setViewMode('edit');
    } catch (err: any) {
      setError(err.message || 'Failed to load inventory details');
    } finally {
      setLoading(false);
    }
  };

  const handleEditImages = async (inventory: InventoryAdminListItem) => {
    try {
      setLoading(true);
      const details = await apiService.getInventoryDetailsForUpdate(inventory.id);
      setSelectedInventory(inventory);
      setSelectedInventoryDetails(details);
      setViewMode('edit-images');
    } catch (err: any) {
      setError(err.message || 'Failed to load inventory details');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleInventory = async (inventory: InventoryAdminListItem) => {
    setSelectedInventory(inventory);
    setViewMode('schedule');
  };

  const handleManageSlots = async (inventory: InventoryAdminListItem) => {
    setSelectedInventory(inventory);
    setViewMode('slots');
  };

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Draft</span>;
      case 1:
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Live</span>;
      case 2:
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Deactivated</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Unknown</span>;
    }
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'create':
        return (
          <CreateInventoryForm
            onSubmit={handleCreateInventory}
            onCancel={() => setViewMode('list')}
            loading={loading}
          />
        );
      case 'edit':
        return selectedInventoryDetails ? (
          <UpdateInventoryForm
            inventory={selectedInventoryDetails}
            onSubmit={(updates) => handleUpdateInventory(selectedInventoryDetails.id, updates)}
            onCancel={() => setViewMode('list')}
            loading={loading}
          />
        ) : null;
      case 'schedule':
        return selectedInventory ? (
          <ScheduleInventoryForm
            inventory={selectedInventory}
            onSubmit={() => {
              setViewMode('list');
              loadInventories();
            }}
            onCancel={() => setViewMode('list')}
            loading={loading}
          />
        ) : null;
      case 'slots':
        return selectedInventory ? (
          <InventorySlotManager
            inventory={selectedInventory}
            onBack={() => setViewMode('list')}
            loading={loading}
          />
        ) : null;
      case 'edit-images':
        return selectedInventoryDetails ? (
          <EditInventoryImagesPage
            inventory={selectedInventoryDetails}
            onBack={() => setViewMode('list')}
            onSave={async (updates) => {
              await handleUpdateInventory(selectedInventoryDetails.id, updates);
              // Reload inventory details after save
              const details = await apiService.getInventoryDetailsForUpdate(selectedInventoryDetails.id);
              setSelectedInventoryDetails(details);
            }}
            loading={loading}
          />
        ) : null;
      default:
        return (
          <InventoryList
            inventories={inventories}
            loading={loading}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            variantFilter={variantFilter}
            onVariantFilterChange={handleVariantFilterChange}
            onEdit={handleEditInventory}
            onEditImages={handleEditImages}
            onDuplicate={(inventory) => {
              const newName = prompt('Enter name for duplicated inventory:', `${inventory.title} (Copy)`);
              if (newName) {
                handleDuplicateInventory(inventory.id, newName);
              }
            }}
            onActivate={handleActivateInventory}
            onDeactivate={handleDeactivateInventory}
            onPublish={handlePublishInventory}
            onSchedule={handleScheduleInventory}
            onManageSlots={handleManageSlots}
            getStatusBadge={getStatusBadge}
          />
        );
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 p-6 pb-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Inventory Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage travel packages, schedules, and bookings
            </p>
          </div>
          {viewMode === 'list' && (
            <button
              onClick={() => setViewMode('create')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Inventory
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 pt-4">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-red-600 hover:text-red-800 text-sm"
            >
              Dismiss
            </button>
          </div>
        )}

        {renderContent()}
      </div>
    </div>
  );
};
